import { useIntl } from '@finn/ui-utils';
import { useCallback, useMemo } from 'react';

import { FilterKey, FiltersResponse, FUEL } from '../../../core';
import { FiltersState, useFiltersResponse } from '../../../filters-management';
import { FilterData, FilterToggleData, TestProps } from '../../helpers';
import { FilterAccordion } from '../atoms/FilterAccordion';
import { FilterOptions } from '../atoms/FilterOptions';
import { RangeSelector } from '../atoms/RangeSelector';

type Props = TestProps & {
  selectedFilters: FiltersState;
  availableFilters: FiltersResponse;
  onOptionClick: (filterObject: FiltersState) => void;
};

export const FinnFilter = ({
  selectedFilters,
  availableFilters,
  onOptionClick,
  testId,
}: Props) => {
  const i18n = useIntl();
  const filtersResponse = useFiltersResponse();

  const selectedColors = useMemo(
    () => selectedFilters[FilterKey.COLORS as keyof FiltersState] || [],
    [selectedFilters]
  );

  const selectedSeats = useMemo(
    () => selectedFilters[FilterKey.SEATS as keyof FiltersState] || [],
    [selectedFilters]
  );

  const selectedDoors = useMemo(
    () => selectedFilters[FilterKey.DOORS as keyof FiltersState] || [],
    [selectedFilters]
  );

  const availableColors = useMemo(() => {
    const newColors = filtersResponse[FilterKey.COLORS]
      .map((configItem) => ({
        value: configItem.id,
        label: configItem.id,
        color: configItem.color_hex,
        selected: (selectedColors as string[])?.includes(configItem.id),
        disabled: false,
      }))
      .filter((item) => Boolean(item.color))
      .sort(
        (a, b) => a.color.localeCompare(b.color) * -1
      ) as FilterToggleData[];

    newColors.unshift({
      value: undefined,
      label: i18n.formatMessage('plp.all'),
      color: undefined,
      selected: !selectedColors,
      disabled: false,
    });

    return newColors;
  }, [
    filtersResponse[FilterKey.COLORS],
    availableFilters[FilterKey.COLORS],
    selectedColors,
  ]);

  const availableSeats = useMemo(() => {
    const newSeats = filtersResponse[FilterKey.SEATS]
      .map((configItem) => ({
        value: configItem,
        label: `${configItem}`,
        selected: (selectedSeats as number[])?.includes(configItem),
        disabled: false,
      }))
      .sort((a, b) => a.value - b.value) as FilterToggleData[];

    return newSeats;
  }, [
    filtersResponse[FilterKey.SEATS],
    availableFilters[FilterKey.SEATS],
    selectedSeats,
  ]);

  const availableDoors = useMemo(() => {
    const newDoors = filtersResponse[FilterKey.DOORS]
      .map((configItem) => ({
        value: configItem,
        label: `${configItem}`,
        selected: (selectedDoors as number[])?.includes(configItem),
        disabled: false,
      }))
      .sort((a, b) => a.value - b.value) as FilterToggleData[];

    return newDoors;
  }, [
    filtersResponse[FilterKey.DOORS],
    availableFilters[FilterKey.DOORS],
    selectedDoors,
  ]);

  const colorOptions = useMemo<FilterData>(
    () => ({
      filterKey: FilterKey.COLORS,
      selectedValues: selectedColors,
      availableFilterValues: availableColors,
    }),
    [filtersResponse[FilterKey.COLORS], availableColors, selectedColors]
  );

  const seatOptions = useMemo<FilterData>(
    () => ({
      filterKey: FilterKey.SEATS,
      selectedValues: selectedSeats,
      availableFilterValues: availableSeats,
    }),
    [filtersResponse[FilterKey.SEATS], availableSeats, selectedSeats]
  );

  const doorOptions = useMemo<FilterData>(
    () => ({
      filterKey: FilterKey.DOORS,
      selectedValues: selectedDoors,
      availableFilterValues: availableDoors,
    }),
    [filtersResponse[FilterKey.DOORS], availableDoors, selectedDoors]
  );

  const handleClick = useCallback(
    (value: object) => {
      onOptionClick(value);
    },
    [onOptionClick]
  );

  const selectedHpRange: [number, number] = useMemo(
    () => [selectedFilters.min_power ?? 0, selectedFilters.max_power ?? 0],
    [selectedFilters.min_power, selectedFilters.max_power]
  );

  const selectedEvRange: [number, number] = useMemo(
    () => [
      selectedFilters.min_ev_range ?? 0,
      selectedFilters.max_ev_range ?? 0,
    ],
    [selectedFilters.min_ev_range, selectedFilters.max_ev_range]
  );

  const isPowerSelected = selectedHpRange.some((item) => item !== undefined);

  const isEvRangeSelected = selectedEvRange.some((item) => item !== undefined);

  return (
    <FilterAccordion title={'Finn Only'} testId={testId}>
      <div className="mb-[6px] mt-3 body-12-semibold">
        {i18n.formatMessage('plp.seats')}
      </div>
      <FilterOptions
        data={seatOptions}
        onClick={handleClick}
        className="grid grid-cols-2"
      />
      <div className="mb-2 mt-6 body-12-semibold">
        {i18n.formatMessage('plp.doors')}
      </div>
      <FilterOptions
        data={doorOptions}
        onClick={handleClick}
        className="grid grid-cols-2"
      />
      <div className="mt-6 body-12-semibold">
        {i18n.formatMessage('plp.color')}
      </div>
      <FilterOptions data={colorOptions} onClick={handleClick} />
      <div className="mb-4 mt-8 body-12-semibold">
        {i18n.formatMessage('plp.performance')}
      </div>
      <RangeSelector
        selectedRange={selectedHpRange}
        availableRange={[
          availableFilters.min_power,
          availableFilters.max_power,
        ]}
        isRangeSelected={isPowerSelected}
        onRangeSelection={onOptionClick}
        minRangeKey={FilterKey.MIN_POWER}
        maxRangeKey={FilterKey.MAX_POWER}
        suffix=" kW"
        minRangeLabel={'Min'}
        maxRangeLabel={'Max'}
      />
      <div className="mb-4 mt-8 body-12-semibold">
        {i18n.formatMessage('plp.evRange')}
      </div>
      <div className="relative">
        {!availableFilters[FilterKey.FUELS].includes(FUEL.ElectricDE) && (
          <div className="absolute left-0 top-0 z-overlay size-full bg-white opacity-50" />
        )}
        <RangeSelector
          selectedRange={selectedEvRange}
          availableRange={[
            availableFilters.min_ev_range,
            availableFilters.max_ev_range,
          ]}
          isRangeSelected={isEvRangeSelected}
          onRangeSelection={onOptionClick}
          minRangeKey={FilterKey.MIN_EV_RANGE}
          maxRangeKey={FilterKey.MAX_EV_RANGE}
          suffix=" km"
          minRangeLabel={'Min'}
          maxRangeLabel={'Max'}
        />
      </div>
    </FilterAccordion>
  );
};
